import { TimeOffStatus, TimeOffType } from '../store/slices/timeOffSlice';
import { dedupeTimeOffEntries } from './timeOffUsage';

export interface UncountedTimeSourceEntry {
  _id?: string;
  id?: string;
  employee?: string | { _id?: string; id?: string };
  startDate: string;
  endDate?: string;
  type: string;
  hours: number;
  status: string;
  notes?: string;
  outlookEventId?: string;
  createdAt?: string;
  updatedAt?: string;
  originalCalendarSubject?: string;
  originalCalendarStart?: string;
  originalCalendarEnd?: string;
}

export interface UncountedTimeEvent {
  id: string;
  date: string;
  type: TimeOffType.PTO | TimeOffType.SICK;
  durationHours: number | null;
  originalCalendarSubject?: string;
  notes?: string;
}

export interface UncountedTimeSummary {
  events: UncountedTimeEvent[];
  occurrences: number;
  ptoOccurrences: number;
  sickOccurrences: number;
  knownDurationHours: number;
  unknownDurationCount: number;
  averageKnownDurationHours: number;
}

export interface UncountedTimePeerComparison {
  rank: number;
  peerCount: number;
  targetIsActive: boolean;
  employeesWithUsage: number;
  occurrenceAverage: number;
  durationAverage: number;
  frequencyPercentile: number;
}

interface EmployeePeer {
  _id: string;
  employmentStatus?: string | null;
}

const getEmployeeId = (entry: UncountedTimeSourceEntry) => {
  if (typeof entry.employee === 'string') return entry.employee;
  return entry.employee?._id || (entry.employee as any)?.id || '';
};

const round = (value: number, precision = 2) => {
  const factor = 10 ** precision;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

const truncate = (value: number, precision = 2) => {
  const factor = 10 ** precision;
  return Math.floor((value + Number.EPSILON) * factor) / factor;
};

const getCalendarDurationHours = (entry: UncountedTimeSourceEntry) => {
  if (!entry.originalCalendarStart || !entry.originalCalendarEnd) return null;
  const start = new Date(entry.originalCalendarStart).getTime();
  const end = new Date(entry.originalCalendarEnd).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  return (end - start) / 3_600_000;
};

const getDurationHours = (entry: UncountedTimeSourceEntry) => {
  const calendarDuration = getCalendarDurationHours(entry);
  // Truncate only for presentation so a true 3.999-hour event never displays
  // as 4.0 hours inside the under-four-hours category.
  return calendarDuration !== null && calendarDuration < 4
    ? truncate(calendarDuration)
    : null;
};

const isActuallyUncounted = (entry: UncountedTimeSourceEntry) => {
  if (Number(entry.hours) !== 0) return false;
  const calendarDuration = getCalendarDurationHours(entry);
  // Calendar imports normalize sub-four-hour entries to zero. Preserve older
  // zero-hour rows with no raw timestamps, but reject inconsistent rows whose
  // timestamps prove the event lasted four hours or more.
  return calendarDuration === null || calendarDuration < 4;
};

export const summarizeUncountedTime = (
  rawEntries: UncountedTimeSourceEntry[],
  year: number
): UncountedTimeSummary => {
  const yearPrefix = `${year}-`;
  const events = dedupeTimeOffEntries(rawEntries)
    .filter((entry) => (
      entry.status === TimeOffStatus.APPROVED &&
      (entry.type === TimeOffType.PTO || entry.type === TimeOffType.SICK) &&
      entry.startDate.startsWith(yearPrefix) &&
      isActuallyUncounted(entry)
    ))
    .map<UncountedTimeEvent>((entry) => ({
      id: entry._id || (entry as any).id || `${entry.startDate}-${entry.type}`,
      date: entry.startDate,
      type: entry.type as TimeOffType.PTO | TimeOffType.SICK,
      durationHours: getDurationHours(entry),
      originalCalendarSubject: entry.originalCalendarSubject,
      notes: entry.notes,
    }))
    .sort((left, right) => right.date.localeCompare(left.date));

  const knownDurations = events
    .map((event) => event.durationHours)
    .filter((duration): duration is number => duration !== null);
  const knownDurationHours = round(knownDurations.reduce((sum, duration) => sum + duration, 0));

  return {
    events,
    occurrences: events.length,
    ptoOccurrences: events.filter((event) => event.type === TimeOffType.PTO).length,
    sickOccurrences: events.filter((event) => event.type === TimeOffType.SICK).length,
    knownDurationHours,
    unknownDurationCount: events.length - knownDurations.length,
    averageKnownDurationHours: knownDurations.length > 0
      ? round(knownDurationHours / knownDurations.length, 1)
      : 0,
  };
};

export const buildUncountedTimePeerComparison = (
  entries: UncountedTimeSourceEntry[],
  employees: EmployeePeer[],
  employeeId: string,
  year: number
): UncountedTimePeerComparison => {
  const activeEmployeeIds = employees
    .filter((employee) => !employee.employmentStatus || employee.employmentStatus === 'active')
    .map((employee) => employee._id);
  const targetIsActive = activeEmployeeIds.includes(employeeId);

  const totals = new Map<string, { occurrences: number; durationHours: number }>(
    activeEmployeeIds.map((id) => [id, { occurrences: 0, durationHours: 0 }])
  );
  const inactiveTargetTotal = { occurrences: 0, durationHours: 0 };

  dedupeTimeOffEntries(entries).forEach((entry) => {
    const id = getEmployeeId(entry);
    const summary = summarizeUncountedTime([entry], year);
    if (summary.occurrences === 0) return;
    const current = totals.get(id);
    if (current) {
      current.occurrences += summary.occurrences;
      current.durationHours += summary.knownDurationHours;
    } else if (!targetIsActive && id === employeeId) {
      inactiveTargetTotal.occurrences += summary.occurrences;
      inactiveTargetTotal.durationHours += summary.knownDurationHours;
    }
  });

  const values = Array.from(totals.values());
  const target = targetIsActive
    ? totals.get(employeeId)!
    : inactiveTargetTotal;
  const strictlyHigher = values.filter((value) => value.occurrences > target.occurrences).length;
  const strictlyLower = values.filter((value) => value.occurrences < target.occurrences).length;
  const comparisonDenominator = targetIsActive ? values.length - 1 : values.length;

  return {
    rank: strictlyHigher + 1,
    peerCount: values.length,
    targetIsActive,
    employeesWithUsage: values.filter((value) => value.occurrences > 0).length,
    occurrenceAverage: values.length > 0
      ? round(values.reduce((sum, value) => sum + value.occurrences, 0) / values.length, 1)
      : 0,
    durationAverage: values.length > 0
      ? round(values.reduce((sum, value) => sum + value.durationHours, 0) / values.length, 1)
      : 0,
    frequencyPercentile: comparisonDenominator > 0
      ? Math.round((strictlyLower / comparisonDenominator) * 100)
      : 0,
  };
};
